import React, { useContext, useState, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import LiveChatWeb from 'react-livechat';

import FeedScreen from '../screens/FeedScreen';
import FeedbackScreen from '../screens/FeedbackScreen';
import CallRepScreen from '../screens/CallRepScreen';
import ProductExploreScreen from '../screens/ProductExploreScreen';
import CartScreen from '../screens/CartScreen';
import NotificationScreen from '../screens/NotificationScreen';
import OrderScreen from '../screens/OrderScreen';
import WalletScreen from '../screens/WalletScreen';
import ChatScreen from '../screens/ChatScreen';
import HistoryScreen from '../screens/HistoryScreen';

import { Platform } from 'react-native';

import { AuthContext } from '../contexts/auth/Store';
import { CartContext } from '../contexts/cart/Store';
import { ProductContext } from '../contexts/product/Store';
import { NotificationContext } from '../contexts/notification/Store';
import { OrderContext } from '../contexts/order/Store';

import { SafeJSONObject } from '../constants/Utils';

import TabBarIcon from '../components/TabBarIcon';
import TabBarText from '../components/TabBarText';

import OrderPromptModal from '../components/modals/OrderPromptModal';
import OrderRatingModal from '../components/modals/OrderRatingModal';
import VersionUpdateModal from '../components/modals/VersionUpdateModal';
import { HistoryContext } from '../contexts/history/Store';
import { getFocusedRouteNameFromRoute, PartialRoute, useNavigation, useRoute } from '@react-navigation/native';
import CartFloatingButton from '../components/cart/CartFloatingButton';
import AddToHomeScreen from '../components/common/AddToHomeScreen';
import HomeNavigator from './HomeNavigator';
import ProductService from '../services/ProductService';
import { IBuyer } from '../interfaces/buyer';
import config from '../constants/Config';
import SessionTokenService from '../services/SessionTokenService';
import { AnalyticsService } from '../constants/Analytics';
import DeviceService from '../services/DeviceService';
import SettingsNavigator from './SettingsNavigator';
import { BalanceScreen } from '../screens/balance';
import PaystackCheckoutScreen from '../screens/PaystackCheckoutScreen';
import OrderNavigator from './OrderNavigator';

const productService = new ProductService()
const sessionTokenService = new SessionTokenService()
const deviceService = new DeviceService()
const analytics = new AnalyticsService()

const BottomTab = createBottomTabNavigator();
const INITIAL_ROUTE_NAME = 'Home';
const TIMER_RANGE: number = 5 * 60 * 1000; // routine task in ms ~20 mins
// const TIMER_RANGE: number = 2000; // routine task in ms ~20 mins

export default function BottomTabNavigator() {
  const navigation = useNavigation();
  const route = useRoute();
  // Set the header title on the parent stack navigator depending on the
  // currently active tab. Learn more in the documentation:
  // https://reactnavigation.org/docs/en/screen-options-resolution.html

  const auth: any = useContext(AuthContext)
  const token = auth.getUserData.userToken;
  const buyer = auth.getUserData.buyerData;
  const { notif_state } = useContext(NotificationContext)
  const [state, dispatch, fetch] = useContext(CartContext)
  const { action: order_fetch } = React.useContext(OrderContext)
  const [history_state, history_dispatch, history_fetch] = useContext(HistoryContext)
  const [cart_state, cart_dispatch, cart_fetch] = useContext(CartContext)
  const { product_fetch } = useContext(ProductContext)

  const [orderModalState, setOrderModalState] = useState(false)
  const [ratingModalState, setRatingModalState] = useState(false)
  const [unratedOrder, setUnratedOrder] = useState(null)
  const [versionModal, setVersionModal] = useState(false)
  const [downloadLink, setDownloadLink] = useState(false)

  const TIMER = useRef<any>(null)

  const hideModal = () => {
    setOrderModalState(false)
  }
  const hideRatingModal = () => {
    setRatingModalState(false)
  }
  const hideVersionModal = () => {
    setVersionModal(false)
  }

  const handleChatLoaded = (ref: any) => {
    try {
      // read ref and hide chat
      ref.on_after_load = function () {
        ref.hide_chat_window()
        auth.setChatInstance(ref)
      }
      ref.on_chat_window_minimized = function () {
        ref.hide_chat_window()
      }
    } catch (e) {
      console.log(e)
    }
  }

  const loadStuff = () => {
    // load products, cart, order, history
    const requests = [
      product_fetch(),
      cart_fetch(),
      order_fetch(),
      history_fetch()
    ]

    Promise.all(requests)
      .then(res => {
      })
      .catch(err => {
        analytics.logError(err)
      })
  }

  const load_background_data = async () => {
    //
    let _buyer: IBuyer = SafeJSONObject(await AsyncStorage.getItem('buyerData'))
    if (_buyer.location_id) {
      productService
        .fetchAllAvailableCategories(_buyer.location_id, config.pagination_limit)
        .then(res => { })
        .catch(err => { })

      productService
        .fetchAndDumpAvailableChunks(_buyer.location_id, config.pagination_limit)
        .then(res => { })
        .catch(err => { })
    }
    
    deviceService.registerDevice()
  }

  const verifyToken = (retry?: number) => {
    sessionTokenService.validateToken().then(res => {
      // so, token is valid? 
      if (res) {
        // yes? do nothing
      } else {
        // no? something happened? network? whatever...
        // do-retry in 10 secs
        if (retry && retry > 0) {
          setTimeout(() => verifyToken(retry - 1), 10 * 1000)
        }
      }
    }).catch(err => {
      // not worthy of continued usage
      auth.signOut()
    })
  }

  useEffect(() => {
    navigation.setOptions({ headerTitle: getHeaderTitle(route) })

    setTimeout(() => {
      loadStuff()
      load_background_data()
      verifyToken(5)
    }, 2000)

    TIMER.current = setInterval(() => {
      loadStuff()
      load_background_data()
    }, TIMER_RANGE)

    return () => {
      // cleanup
      if (TIMER.current) {
        clearInterval(TIMER.current)
      }
    }

  }, [])

  return (
    <>
      {
        downloadLink
        && <VersionUpdateModal hideModal={hideVersionModal} modalState={versionModal} downloadLink={downloadLink} />
      }
      {
        buyer && Platform.OS === 'web'
          ? <LiveChatWeb
            license={config.livechat_id}
            visitor={{
              name: buyer?.name,
              phone: buyer?.phone,
            }}
            params={[
              { name: 'User ID', value: buyer?.user_id },
              { name: 'Buyer ID', value: buyer?._id },
              { name: 'Phone', value: buyer?.phone },
              { name: 'Store Name', value: buyer?.name },
              { name: 'Store Location', value: buyer?.location.name },
              { name: 'Store Address', value: buyer?.address }
            ]}
            onChatLoaded={handleChatLoaded}
          />
          : null
      }
      <OrderPromptModal hideModal={hideModal} modalState={orderModalState} />
      <OrderRatingModal hideModal={hideRatingModal} modalState={ratingModalState} order={unratedOrder} />
      <AddToHomeScreen />
      <CartFloatingButton />
      <BottomTab.Navigator
        // headerMode='screen'
        initialRouteName={INITIAL_ROUTE_NAME}
        backBehavior={'initialRoute'}
        tabBarOptions={
          {
            style: {
              height: 48,
              minHeight: 48,
              paddingHorizontal: 0,
              paddingVertical: 4,
            },
            tabStyle: {
              flexDirection: 'column',
              alignContent: 'center',
              justifyContent: 'center',
              alignItems: 'center',
            },
            keyboardHidesTabBar: false,
            iconStyle: {
              maxHeight: 24,
              marginBottom: 2,
            }
          }
        }
      >
        <BottomTab.Screen
          name="Home"
          component={HomeNavigator}
          options={{
            title: "Suplias Now",
            tabBarIcon: ({ focused }) => <TabBarIcon focused={focused} name="products" />,
            tabBarLabel: ({ focused, color }) => <TabBarText focused={focused} title="Products" />
          }}
        />
        <BottomTab.Screen
          name="Feedback"
          component={FeedbackScreen}
          options={{
            title: 'Feedback - Suplias Now',
            tabBarButton: () => null,
          }}
        />
        <BottomTab.Screen
          name="Feed"
          component={FeedScreen}
          options={{
            title: 'Promotion - Suplias Now',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="CallRep"
          component={CallRepScreen}
          options={{
            title: 'call-rep',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="Order"
          component={OrderNavigator}
          options={{
            tabBarIcon: ({ focused }) => <TabBarIcon focused={focused} name="invoices" />,
            tabBarLabel: ({ focused, color }) => <TabBarText focused={focused} title="My Invoices" />
          }}
        />
        <BottomTab.Screen
          name="ProductExplore"
          component={ProductExploreScreen}
          options={{
            title: 'Find Products - Suplias Now',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="Cart"
          component={CartScreen}
          options={{
            title: 'Cart - Suplias Now',
            tabBarIcon: ({ focused }) => <TabBarIcon focused={focused} name="cart" badgeCount={state.order_group_items && state.order_group_items.length ? state.order_group_items.length : null} />,
            tabBarLabel: ({ focused, color }) => <TabBarText focused={focused} title="Cart" />
          }}
        />

        <BottomTab.Screen
          name="Notification"
          component={NotificationScreen}
          options={{
            title: 'Notifications - Suplias Now',
            tabBarIcon: ({ focused }) => <TabBarIcon focused={focused} name="md-notifications" notifications={notif_state.notifications} />,
            tabBarLabel: ({ focused, color }) => <TabBarText focused={focused} title="Notifications" />,
            tabBarButton: () => null,
          }}
        />
        <BottomTab.Screen
          name="Settings"
          component={SettingsNavigator}
          options={{
            title: 'Settings - Suplias Now',
            tabBarIcon: ({ focused }) => <TabBarIcon focused={focused} name="account" />,
            tabBarLabel: ({ focused, color }) => <TabBarText focused={focused} title="Settings" />
          }}
        />
        <BottomTab.Screen
          name="Wallet"
          component={WalletScreen}
          options={{
            title: 'Wallet - Suplias Now',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="Chat"
          component={ChatScreen}
          options={{
            title: 'Chat - Suplias Now',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="History"
          component={HistoryScreen}
          options={{
            title: 'History - Suplias Now',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="Balance"
          component={BalanceScreen}
          options={{
            title: 'Balance - Suplias Now',
            tabBarButton: () => null
          }}
        />
        <BottomTab.Screen
          name="PaystackCheckout"
          component={PaystackCheckoutScreen}
          options={{
            title: 'Paystack Checkout - Suplias Now',
            tabBarButton: () => null
          }}
        />
      </BottomTab.Navigator>
    </>
  );
}

function getHeaderTitle(route: PartialRoute<any>) {
  // const routeName = route.state?.routes[route.state.index]?.name ?? INITIAL_ROUTE_NAME;
  const routeName: string = getFocusedRouteNameFromRoute(route) ?? INITIAL_ROUTE_NAME;

  switch (routeName) {
    case 'Home':
      return 'Buy Products - Suplias Now';
    case 'Cart':
      return 'Cart - Suplias Now';
    case 'ProductExplore':
      return 'Find Products - Suplias Now';
    default:
      return 'Suplias Now';
  }
}
